/**
 * profileUtils — 档案相关纯函数
 *
 * 提供档案身份识别、比较、默认判定、有效性检查等逻辑，
 * 供 App.js、BasicInfo、NutritionAssessment 等页面使用。
 *
 * @module utils/profileUtils
 */

/** 无档案时的占位 ID */
export const NONE_PROFILE_ID = "none";

/** 需要绑定档案才能使用的功能映射 */
export const PROFILE_REQUIRED_FEATURES = {
    meal_plan: "智能配餐",
    assessment: "营养评估",
    nutrition_manage: "营养管理",
    recipe_customization: "食谱定制",
    ketogenic_diet: "生酮饮食",
    sport_nutrition: "运动营养",
    growth_development: "生长发育",

};

/** 全部疾病标签枚举（用于 personInfo 构建） */
export const ALL_DISEASES = [
    "孤独症 (ASD)",
    "抽动障碍 (TD)",
    "注意缺陷多动障碍 (ADHD)",
    "身高发育障碍",
    "牛奶蛋白过敏",
];

/**
 * 获取档案唯一身份标识（优先 profileCode，其次 id）
 * @param {object} profile
 * @returns {string}
 */
export const getProfileIdentity = (profile) =>
    String(profile?.profileCode || profile?.id || "");

/**
 * 判断两个档案是否为同一人
 * @param {object} left
 * @param {object} right
 * @returns {boolean}
 */
export const isSameProfile = (left, right) =>
    !!getProfileIdentity(left) &&
    getProfileIdentity(left) === getProfileIdentity(right);

/**
 * 判断是否为默认档案
 * @param {object} profile
 * @returns {boolean}
 */
export const isDefaultProfile = (profile) => {
    const value =
        profile?.defaultProfile ?? profile?.isDefault ?? profile?.defaultFlag;
    return [true, 1, "1", "true", "TRUE", "Y", "YES"].includes(value);
};

/**
 * 判断档案是否可选（未被删除且年龄不超过 15 岁）
 * @param {object} profile
 * @returns {boolean}
 */
export const isSelectableProfile = (profile) =>
    String(profile?.status) !== "0" &&
    !(Number.parseInt(profile?.profileAge, 10) > 15);

/**
 * 判断档案记录是否有效（有真实 ID 且包含 profileCode/code）
 * @param {object} profile
 * @returns {boolean}
 */
export const hasValidProfileRecord = (profile) =>
    Boolean(
        profile &&
        profile.id !== NONE_PROFILE_ID &&
        (profile.profileCode || profile.code),
    );

/**
 * 从档案对象构建 personInfo（用于 AI 对话上下文）
 * @param {object} profile
 * @returns {object}
 */
export const buildPersonInfoFromProfile = (profile) => {
    if (!profile) return null;

    const tags = (profile.healthRequire || "").split("、").filter(Boolean);

    const profileDiseases = tags
        .filter((t) => ALL_DISEASES.includes(t))
        .join("、");

    const profileNeed =
        tags.find((t) => !ALL_DISEASES.includes(t)) ||
        (profileDiseases ? "神经/生长发育障碍特殊需求" : "无特殊需求");

    const ageStr = profile.profileAge || "";
    const monthMatch = ageStr.match(/(\d+)个月/);
    const extractedMonth = monthMatch ? parseInt(monthMatch[1]) : 0;

    return {
        age: parseInt(profile.profileAge) || 0,
        month: extractedMonth,
        gender: profile.profileSex === "1" ? "男" : "女",
        height: profile.profileHeight,
        weight: profile.profileWeight,
        health_need: profileNeed,
        tell_disease: profileDiseases || "",
    };
};
